<template>
  <div class="settings-page">
    <!-- 左侧导航栏 -->
    <aside class="settings-sidebar">
      <nav class="sidebar-nav">
        <div
          v-for="(item, index) in sidebarMenu"
          :key="index"
          class="nav-item"
          :class="{ active: activeSection === item.id }"
          @click="scrollToSection(item.id)"
        >
          <img :src="item.image" alt="" class="nav-icon" />
          <span>{{ item.name }}</span>
        </div>
      </nav>
    </aside>

    <!-- 右侧内容区 -->
    <main class="settings-main">
      <!-- 账号管理 -->
      <section id="account" class="settings-section" ref="account">
      <h3 class="section-title" :class="{ active: activeSection === 'account' }">账号管理</h3>
      <div class="settings-list">
        <!-- 编辑资料 -->
        <div class="setting-item" @click="goToEditProfile">
          <div class="item-left">
            <span class="item-title">编辑资料</span>
            <span class="item-desc">修改您的基本信息</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <!-- 账户安全 -->
        <div class="setting-item" @click="goToSecurity">
          <div class="item-left">
            <span class="item-title">账户安全</span>
            <span class="item-desc">修改密码</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- 推送通知 -->
    <section id="notification" class="settings-section" ref="notification">
      <h3 class="section-title" :class="{ active: activeSection === 'notification' }">推送通知</h3>
      <div class="settings-list">
        <!-- 桌面通知 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">桌面通知</span>
            <span class="item-desc">允许在桌面推送互动、新视频等内容的通知，您随时可以关闭</span>
          </div>
          <div class="item-right">
            <div class="custom-switch" :class="{ active: settings.desktopNotify }" @click.stop="settings.desktopNotify = !settings.desktopNotify">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 互动通知 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">互动通知</span>
            <span class="item-desc">点赞、评论、粉丝和关注等</span>
          </div>
          <div class="item-right">
            <div class="custom-switch" :class="{ active: settings.interactionNotify }" @click.stop="settings.interactionNotify = !settings.interactionNotify">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 隐私 -->
    <section id="privacy" class="settings-section" ref="privacy">
      <h3 class="section-title" :class="{ active: activeSection === 'privacy' }">隐私</h3>
      <div class="settings-list">
        <!-- 账号可见性 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">账号可见性</span>
            <span class="item-desc">私密账号开关，仅与您互相关注的好友可见</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.accountPrivate ? '私密' : '公开' }}</span>
            <div class="custom-switch" :class="{ active: settings.accountPrivate }" @click.stop="settings.accountPrivate = !settings.accountPrivate">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 内容可见性 -->
        <div class="setting-item" @click="goToContentVisibility">
          <div class="item-left">
            <span class="item-title">内容可见性</span>
            <span class="item-desc">您的观看历史、收藏、已赞内容是否公开</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.contentVisibility }}</span>
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <!-- 互动 - 评论 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">评论</span>
            <span class="item-desc">谁可以对你的作品发表评论</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.commentPermission }}</span>
            <div class="custom-switch" :class="{ active: settings.commentPermission === '所有人' }" @click.stop="toggleCommentPermission">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 互动 - 私信 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">私信</span>
            <span class="item-desc">谁可以发消息给你</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.messagePermission }}</span>
            <div class="custom-switch" :class="{ active: settings.messagePermission === '所有人' }" @click.stop="toggleMessagePermission">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 隐私协议 -->
        <div class="setting-item" @click="goToPrivacyPolicy">
          <div class="item-left">
            <span class="item-title">隐私协议</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- 钱包 -->
    <section id="wallet" class="settings-section" ref="wallet">
      <h3 class="section-title" :class="{ active: activeSection === 'wallet' }">钱包</h3>
      <div class="settings-list">
        <!-- 余额及充值 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">余额及充值</span>
            <!-- <span class="item-balance">{{ wallet.balance }}</span> -->
          </div>
          <div class="item-right">
            <button class="recharge-btn" @click="recharge">充值</button>
          </div>
        </div>
        <!-- 兑换礼物 -->
        <!-- <div class="setting-item" @click="goToExchangeGifts">
          <div class="item-left">
            <span class="item-title">兑换礼物</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div> -->
      </div>
    </section>

    <!-- 未成年人模式 -->
    <section id="teenager" class="settings-section" ref="teenager">
      <h3 class="section-title" :class="{ active: activeSection === 'teenager' }">未成年人模式</h3>
      <div class="settings-list">
        <!-- 开启未成年人模式 -->
        <div class="setting-item" @click="toggleTeenagerMode">
          <div class="item-left">
            <span class="item-title">开启未成年人模式</span>
            <span class="item-desc">开启后，将限制使用时长、打赏功能，并过滤不适宜内容</span>
          </div>
          <div class="item-right">
            <div class="custom-switch" :class="{ active: settings.teenagerMode }" @click.stop="toggleTeenagerMode">
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 使用时长限制 -->
        <!-- <div class="setting-item" v-if="settings.teenagerMode">
          <div class="item-left">
            <span class="item-title">每日使用时长</span>
            <span class="item-desc">设置每日最大使用时长</span>
          </div>
          <div class="item-right">
            <span class="item-value">{{ settings.teenagerTimeLimit }}分钟</span>
          </div>
        </div> -->
        <!-- 未成年人模式说明 -->
        <!-- <div class="setting-item" @click="goToTeenagerDesc">
          <div class="item-left">
            <span class="item-title">未成年人模式说明</span>
            <span class="item-desc">了解未成年人模式的功能和限制</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div> -->
      </div>
    </section>

    <!-- 意见反馈与帮助 -->
    <section id="feedback" class="settings-section" ref="feedback">
      <h3 class="section-title" :class="{ active: activeSection === 'feedback' }">意见反馈与帮助</h3>
      <div class="settings-list">
        <!-- 意见反馈 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">意见反馈</span>
            <div class="item-contact">
              <a :href="'mailto:' + contact.feedbackEmail" class="item-link">{{ contact.feedbackEmail }}</a>
              <span class="copy-btn" @click="copyEmail(contact.feedbackEmail)">复制</span>
            </div>
          </div>
        </div>
        <!-- 帮助 -->
        <div class="setting-item">
          <div class="item-left">
            <span class="item-title">帮助</span>
            <div class="item-contact">
              <a :href="'mailto:' + contact.helpEmail" class="item-link">{{ contact.helpEmail }}</a>
              <span class="copy-btn" @click="copyEmail(contact.helpEmail)">复制</span>
            </div>
          </div>
        </div>
        <!-- 用户协议 -->
        <div class="setting-item" @click="goToUserAgreement">
          <div class="item-left">
            <span class="item-title">用户协议</span>
          </div>
          <div class="item-right">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </section>
    </main>

    <!-- 编辑资料弹窗 -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>编辑资料</h3>
          <i class="el-icon-close" @click="closeEditModal"></i>
        </div>
        <div class="modal-body">
          <!-- 用户昵称 -->
          <div class="form-item">
            <label>用户昵称</label>
            <input v-model="editForm.nickname" type="text" placeholder="请输入昵称" />
          </div>
          <!-- 账号信息 -->
          <div class="form-item">
            <label>账号信息</label>
            <div class="account-info">
              <div class="account-left">
                <span class="account-text">账号：{{ editForm.email }}</span>
              </div>
              <div class="account-right">
                <span class="account-id">ID：{{ editForm.userId }}</span>
                <span class="copy-btn" @click="copyId(editForm.userId)">复制</span>
              </div>
            </div>
          </div>
          <!-- 性别 -->
          <div class="form-item">
            <label>性别</label>
            <div class="gender-options">
              <label class="radio-label">
                <input v-model="editForm.gender" type="radio" value="male" />
                <span class="radio-custom"></span>
                <span>男</span>
              </label>
              <label class="radio-label">
                <input v-model="editForm.gender" type="radio" value="female" />
                <span class="radio-custom"></span>
                <span>女</span>
              </label>
            </div>
          </div>
          <!-- 生日 -->
          <div class="form-item">
            <label>生日</label>
            <div class="date-input">
              <input v-model="editForm.birthday" type="date" />
              <i class="el-icon-date"></i>
            </div>
          </div>
          <!-- 自我介绍 -->
          <div class="form-item">
            <label>自我介绍</label>
            <textarea v-model="editForm.bio" rows="4" placeholder="请输入自我介绍"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary" @click="saveProfile">保存</button>
          <button class="btn-default" @click="closeEditModal">取消</button>
        </div>
      </div>
    </div>

    <!-- 账户安全弹窗 -->
    <div v-if="showSecurityModal" class="modal-overlay" @click.self="closeSecurityModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>账户安全</h3>
          <i class="el-icon-close" @click="closeSecurityModal"></i>
        </div>
        <div class="modal-body">
          <!-- 原密码 -->
          <div class="form-item">
            <label>原密码</label>
            <input v-model="securityForm.oldPassword" type="password" placeholder="请输入原密码" />
          </div>
          <!-- 新密码 -->
          <div class="form-item">
            <label>新密码</label>
            <input v-model="securityForm.newPassword" type="password" placeholder="请输入新密码" />
          </div>
          <!-- 确认密码 -->
          <div class="form-item">
            <label>确认密码</label>
            <input v-model="securityForm.confirmPassword" type="password" placeholder="请再次输入新密码" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary" @click="savePassword">保存</button>
          <button class="btn-default" @click="closeSecurityModal">取消</button>
        </div>
      </div>
    </div>

    <!-- ========== 未成年人模式密码弹窗 ========== -->
    <div v-if="showTeenagerPasswordModal" class="modal-overlay" @click.self="closeTeenagerPasswordModal">
      <div class="modal-content teenager-modal-v2">
        <!-- 弹窗头部：图标 + 标题 -->
        <div class="teenager-modal-header">
          <div class="teenager-icon">
            <img src="../../assets/qsn.png" alt="未成年人模式" />
          </div>
          <h3>
            <span v-if="teenagerModalMode === 'set'">开启未成年人模式</span>
            <span v-else>关闭未成年人模式</span>
          </h3>
          <i class="el-icon-close close-btn" @click="closeTeenagerPasswordModal"></i>
        </div>
        <!-- 弹窗主体 -->
        <div class="teenager-modal-body">
          <!-- 设置密码模式：开启 -->
          <template v-if="teenagerModalMode === 'set'">
            <!-- 功能介绍列表 -->
            <div class="teenager-features">
              <div class="feature-item">
                <div class="feature-icon icon-shield">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <div class="feature-title">防止沉迷 安全护航</div>
                  <div class="feature-desc">默认每天最多使用40分钟、晚上22时-次日6时无法使用充值功能</div>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon icon-users">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <div class="feature-title">分龄定制 更懂孩子</div>
                  <div class="feature-desc">细分年龄段推荐适龄主题内容，更精准满足孩子身心健康成长需要</div>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon icon-video">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 6h-7.59l3.29-3.29L16 2l-4 4-4-4-.7.71L10.59 6H3c-1.1 0-2 .89-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm7.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm3-3c-.83 0-1.5-.67-1.5-1.5S20.67 9 21.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <div class="feature-title">海量知识 妙趣横生</div>
                  <div class="feature-desc">传统乐器、国风音乐，各类应有尽有</div>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon icon-star">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <div class="feature-title">多彩内容 健康娱乐</div>
                  <div class="feature-desc">题材丰富、形式新颖的内容触手可及</div>
                </div>
              </div>
            </div>
            <p class="teenager-subdesc">请设置4位数字密码，用于关闭时验证</p>
            <!-- 4位密码输入框 -->
            <div class="password-input-container" @click="focusPasswordInput">
              <div class="password-dots">
                <div
                  v-for="(dot, index) in 4"
                  :key="index"
                  class="password-dot"
                  :class="{ filled: teenagerPassword.length > index, active: teenagerPassword.length === index }"
                >
                  <span v-if="teenagerPassword.length > index">●</span>
                </div>
              </div>
              <!-- 隐藏的真实输入框 -->
              <input
                ref="teenagerPasswordInput"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                maxlength="4"
                class="hidden-input"
                @keyup="handlePasswordKeyup('teenagerPassword', $event)"
              />
            </div>
          </template>
          <!-- 验证密码模式：关闭 -->
          <template v-else>
            <!-- 功能介绍列表 -->
            <div class="teenager-features">
              <div class="feature-item">
                <div class="feature-icon icon-shield">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <div class="feature-title">防止沉迷 安全护航</div>
                  <div class="feature-desc">默认每天最多使用40分钟、晚上22时-次日6时无法使用充值功能</div>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon icon-users">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <div class="feature-title">分龄定制 更懂孩子</div>
                  <div class="feature-desc">细分年龄段推荐适龄主题内容，更精准满足孩子身心健康成长需要</div>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon icon-video">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 6h-7.59l3.29-3.29L16 2l-4 4-4-4-.7.71L10.59 6H3c-1.1 0-2 .89-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm7.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm3-3c-.83 0-1.5-.67-1.5-1.5S20.67 9 21.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <div class="feature-title">海量知识 妙趣横生</div>
                  <div class="feature-desc">传统乐器、国风音乐，各类应有尽有</div>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon icon-star">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <div class="feature-title">多彩内容 健康娱乐</div>
                  <div class="feature-desc">题材丰富、形式新颖的内容触手可及</div>
                </div>
              </div>
            </div>
            <p class="teenager-subdesc">请输入密码以关闭未成年人模式</p>
            <!-- 4位密码输入框 -->
            <div class="password-input-container" @click="focusPasswordInput">
              <div class="password-dots">
                <div
                  v-for="(dot, index) in 4"
                  :key="index"
                  class="password-dot"
                  :class="{ filled: teenagerPasswordVerify.length > index, active: teenagerPasswordVerify.length === index }"
                >
                  <span v-if="teenagerPasswordVerify.length > index">●</span>
                </div>
              </div>
              <!-- 隐藏的真实输入框 -->
              <input
                ref="teenagerPasswordInput"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                maxlength="4"
                class="hidden-input"
                @keyup="handlePasswordKeyup('teenagerPasswordVerify', $event)"
              />
            </div>
          </template>
          <!-- 错误提示信息 -->
          <p v-if="teenagerPasswordError" class="teenager-error">{{ teenagerPasswordError }}</p>
        </div>
        <!-- 弹窗底部按钮 -->
        <div class="teenager-modal-footer">
          <!-- 设置密码模式：显示确认按钮 -->
          <template v-if="teenagerModalMode === 'set'">
            <button
              class="btn-teenager-primary"
              :disabled="teenagerPassword.length !== 4"
              @click="confirmTeenagerPassword"
            >
              确认开启
            </button>
            <button class="btn-teenager-default" @click="closeTeenagerPasswordModal">取消</button>
          </template>
          <!-- 验证密码模式：显示确认关闭按钮 -->
          <template v-else>
            <button
              class="btn-teenager-primary"
              :disabled="teenagerPasswordVerify.length !== 4"
              @click="verifyAndCloseTeenagerMode"
            >
              确认关闭
            </button>
            <button class="btn-teenager-default" @click="closeTeenagerPasswordModal">取消</button>
          </template>
          <router-link to="/wcnr" style="text-align: center; cursor: pointer; text-decoration: none; font-size: 13px;">《未成年人保护协议》</router-link>
        </div>
      </div>
    </div>

    <!-- ========== 内容可见性设置弹窗 ========== -->
    <div v-if="showContentVisibilityModal" class="modal-overlay" @click.self="closeContentVisibilityModal">
      <div class="modal-content visibility-modal">
        <!-- 弹窗头部 -->
        <div class="modal-header">
          <h3>内容可见性</h3>
          <i class="el-icon-close" @click="closeContentVisibilityModal"></i>
        </div>
        <!-- 弹窗主体：单选列表 -->
        <div class="modal-body">
          <p class="modal-desc">设置您的观看历史、收藏、已赞内容是否公开</p>
          <div class="visibility-options">
            <!-- 公开选项 -->
            <div
              class="visibility-option"
              :class="{ active: tempContentVisibility === '公开' }"
              @click="selectContentVisibility('公开')"
            >
              <div class="option-icon">
                <i class="el-icon-view"></i>
              </div>
              <div class="option-info">
                <span class="option-title">公开</span>
                <span class="option-desc">所有人都可以看到您的内容</span>
              </div>
              <div class="option-check">
                <i v-if="tempContentVisibility === '公开'" class="el-icon-check"></i>
              </div>
            </div>
            <!-- 私密选项 -->
            <div
              class="visibility-option"
              :class="{ active: tempContentVisibility === '私密' }"
              @click="selectContentVisibility('私密')"
            >
              <div class="option-icon">
                <i class="el-icon-lock"></i>
              </div>
              <div class="option-info">
                <span class="option-title">私密</span>
                <span class="option-desc">仅自己可见</span>
              </div>
              <div class="option-check">
                <i v-if="tempContentVisibility === '私密'" class="el-icon-check"></i>
              </div>
            </div>
            <!-- 仅互关选项 -->
            <div
              class="visibility-option"
              :class="{ active: tempContentVisibility === '仅互关' }"
              @click="selectContentVisibility('仅互关')"
            >
              <div class="option-icon">
                <i class="el-icon-user"></i>
              </div>
              <div class="option-info">
                <span class="option-title">仅互关</span>
                <span class="option-desc">仅互相关注的好友可见</span>
              </div>
              <div class="option-check">
                <i v-if="tempContentVisibility === '仅互关'" class="el-icon-check"></i>
              </div>
            </div>
          </div>
        </div>
        <!-- 弹窗底部按钮 -->
        <div class="modal-footer">
          <button class="btn-primary" @click="saveContentVisibility">保存</button>
          <button class="btn-default" @click="closeContentVisibilityModal">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
export default {
  name: 'SettingsPage',
  data() {
    return {
      // 当前激活的区块
      activeSection: 'account',
      // 是否正在手动滚动（点击导航时禁用滚动监听）
      isScrolling: false,
      // ========== 未成年人模式密码弹窗 ==========
      // 未成年人模式密码弹窗显示状态
      showTeenagerPasswordModal: false,
      // 当前弹窗模式：'set'设置密码 / 'verify'验证关闭
      teenagerModalMode: 'set',
      // 未成年人模式密码输入（4位数字）
      teenagerPassword: '',
      // 未成年人模式密码验证输入（关闭时用）
      teenagerPasswordVerify: '',
      // 密码错误提示信息
      teenagerPasswordError: '',
      // ========== 内容可见性弹窗 ==========
      // 内容可见性弹窗显示状态
      showContentVisibilityModal: false,
      // 内容可见性临时选择值
      tempContentVisibility: '私密',
      // 侧边栏菜单
      sidebarMenu: [
        { id: 'account', name: '账号管理', image: require('../../assets/zh2.png') },
        { id: 'notification', name: '推送通知', image: require('../../assets/tz2.png') },
        { id: 'privacy', name: '隐私', image: require('../../assets/ys2.png') },
        { id: 'wallet', name: '钱包', image: require('../../assets/xb2.png') },
        { id: 'teenager', name: '未成年人模式', image: require('../../assets/qsn.png') },
        { id: 'feedback', name: '意见反馈与帮助', image: require('../../assets/yh2.png') }
      ],
      // 弹窗显示状态
      showEditModal: false,
      showSecurityModal: false,
      // 编辑资料表单 - 从Vuex获取
      editForm: {
        nickname: '', // nicheng
        email: '', // username(账号)
        userId: '', // id
        gender: '', // gender(男/女)
        birthday: '', // birthday
        bio: '' // content(自我介绍)
      },
      // 修改密码表单
      securityForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      // 设置项数据
      settings: {
        // 桌面通知开关
        desktopNotify: true,
        // 互动通知开关
        interactionNotify: true,
        // 账号是否私密
        accountPrivate: true,
        // 内容可见性
        contentVisibility: '私密',
        // 评论权限
        commentPermission: '所有人',
        // 私信权限
        messagePermission: '所有人',
        // 未成年人模式
        teenagerMode: false,
        // 每日使用时长限制（分钟）
        teenagerTimeLimit: 40
      },
      // 钱包信息
      wallet: {
        balance: 20
      },
      // 联系邮箱
      contact: {
        feedbackEmail: 'gao_ming163@163.com',
        helpEmail: '86+18611573207'
      }
    }
  },
  mounted() {
    // 监听滚动，更新当前激活的区块
    this.initScrollListener()
    // ========== 未成年人模式初始化 ==========
    // 从localStorage读取未成年人模式状态
    const savedTeenagerMode = localStorage.getItem('teenagerMode')
    if (savedTeenagerMode === null) {
      // 首次使用，localStorage中没有数据，强制设置为关闭状态
      this.settings.teenagerMode = false
      localStorage.setItem('teenagerMode', 'false')
    } else {
      // 有数据，根据存储值设置（'true'为开启，其他为关闭）
      this.settings.teenagerMode = savedTeenagerMode === 'true'
    }
    // 同步到Vuex
    this.$store.commit('setTeenagerMode', this.settings.teenagerMode)
    // ========== 内容可见性初始化 ==========
    // 从localStorage读取内容可见性设置
    const savedVisibility = localStorage.getItem('contentVisibility')
    if (savedVisibility === null) {
      // 首次使用，默认设置为'私密'
      this.settings.contentVisibility = '私密'
      localStorage.setItem('contentVisibility', '私密')
    } else {
      this.settings.contentVisibility = savedVisibility
    }
    // ========== 检查是否需要自动打开未成年人模式弹窗 ==========
    if (this.$route.query.openTeenagerModal === 'true') {
      // 滚动到未成年人模式区块
      this.$nextTick(() => {
        this.scrollToSection('teenager')
        // 根据当前状态决定打开设置密码弹窗还是验证密码弹窗
        if (this.settings.teenagerMode) {
          // 已开启，打开验证密码弹窗（关闭模式）
          this.openTeenagerVerifyModal()
        } else {
          // 未开启，打开设置密码弹窗（开启模式）
          this.openTeenagerSetModal()
        }
      })
    }
  },
  beforeDestroy() {
    // 移除滚动监听
    this.removeScrollListener()
  },
  methods: {
    // 初始化滚动监听
    initScrollListener() {
      const main = document.querySelector('.settings-main')
      if (main) {
        main.addEventListener('scroll', this.handleScroll)
      }
    },
    // 移除滚动监听
    removeScrollListener() {
      const main = document.querySelector('.settings-main')
      if (main) {
        main.removeEventListener('scroll', this.handleScroll)
      }
    },
    // 处理滚动事件
    handleScroll() {
      // 如果正在手动滚动，不处理
      if (this.isScrolling) return
      
      const sections = ['account', 'notification', 'privacy', 'wallet', 'teenager', 'feedback']
      const main = document.querySelector('.settings-main')
      if (!main) return

      const scrollTop = main.scrollTop
      const offset = 50 // 偏移量

      for (const sectionId of sections) {
        const section = this.$refs[sectionId]
        if (section) {
          const sectionTop = section.offsetTop
          const sectionHeight = section.offsetHeight
          if (scrollTop >= sectionTop - offset && scrollTop < sectionTop + sectionHeight - offset) {
            this.activeSection = sectionId
            break
          }
        }
      }
    },
    // 滚动到指定区块
    scrollToSection(sectionId) {
      this.activeSection = sectionId
      // 标记正在手动滚动，禁用滚动监听
      this.isScrolling = true
      const section = this.$refs[sectionId]
      if (section) {
        const main = document.querySelector('.settings-main')
        if (main) {
          main.scrollTo({
            top: section.offsetTop - 40,
            behavior: 'smooth'
          })
          // 滚动动画完成后恢复滚动监听（约500ms）
          setTimeout(() => {
            this.isScrolling = false
          }, 500)
        }
        // 触发对应标题的放大动画
        this.triggerTitleAnimation(section)
      }
    },
    // 触发标题放大动画
    triggerTitleAnimation(section) {
      const title = section.querySelector('.section-title')
      if (title) {
        // 移除动画类（如果存在）
        title.classList.remove('title-scale-animation')
        // 使用 requestAnimationFrame 确保在下一帧添加动画类
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            title.classList.add('title-scale-animation')
            // 动画结束后移除类
            setTimeout(() => {
              title.classList.remove('title-scale-animation')
            }, 300)
          })
        })
      }
    },
    // 打开编辑资料弹窗 - 从Vuex获取用户信息
    goToEditProfile() {
      // 从Vuex获取LoginStorage
      const userInfo = this.$store.state.LoginStorage
      if (userInfo && userInfo !== -1) {
        // 映射字段: nickname->nicheng, email->username, userId->id, gender->gender, birthday->birthday, bio->content
        this.editForm.nickname = userInfo.nickname || ''
        this.editForm.email = userInfo.username || ''
        this.editForm.userId = userInfo.id || ''
        // gender转换: 后端是"男"/"女"，前端是"male"/"female"
        this.editForm.gender = userInfo.gender === '男' ? 'male' : (userInfo.gender === '女' ? 'female' : '')
        this.editForm.birthday = userInfo.birthday || ''
        this.editForm.bio = userInfo.content || ''
      }
      this.showEditModal = true
    },
    // 关闭编辑资料弹窗
    closeEditModal() {
      this.showEditModal = false
    },
    // 保存资料 - 调用接口
    saveProfile() {
      // 构建请求参数
      const params = {
        username: this.editForm.email, // 账号(必传)
        nicheng: this.editForm.nickname || undefined, // 昵称
        gender: this.editForm.gender === 'male' ? '男' : (this.editForm.gender === 'female' ? '女' : undefined), // 性别转换
        content: this.editForm.bio || undefined, // 自我介绍
        birthday: this.editForm.birthday || undefined // 生日
      }
      
      // 使用axios发送请求
      axios.post('https://ttxq.xiangyouhulian.cn/api/index/xiugai', params).then(res => {
       console.log(res.data.data);
        if(res.data.code== 1){
          this.$message.success('保存成功')
          // 更新Vuex中的用户信息
          this.$store.commit('LoginModification', res.data.data);
          //存储到本地浏览器中
          localStorage.setItem('TTxq', JSON.stringify(res.data.data));
        }else{
          this.$message.error(res.data.msg)
        }
      }).catch(error => {
        this.$message.error('网络错误，请稍后重试')
        console.error('保存资料失败:', error)
      })
    },
    // 复制ID
    copyId(id) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(id).then(() => {
          this.$message.success('已复制')
        })
      } else {
        const input = document.createElement('input')
        input.value = id
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$message.success('已复制')
      }
    },
    // 打开账户安全弹窗
    goToSecurity() {
      this.showSecurityModal = true
    },
    // 关闭账户安全弹窗
    closeSecurityModal() {
      this.showSecurityModal = false
    },
    // 保存密码
    savePassword() {
      if (this.securityForm.newPassword !== this.securityForm.confirmPassword) {
        this.$message.error('两次输入的密码不一致')
        return
      }
      this.$emit('savePassword', this.securityForm)
      this.securityForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.showSecurityModal = false
    },
    // ========== 内容可见性设置 ==========
    /**
     * 打开内容可见性设置弹窗
     */
    goToContentVisibility() {
      // 将当前设置值赋给临时变量，用于弹窗内选择
      this.tempContentVisibility = this.settings.contentVisibility
      // 显示弹窗
      this.showContentVisibilityModal = true
    },
    /**
     * 关闭内容可见性弹窗
     */
    closeContentVisibilityModal() {
      this.showContentVisibilityModal = false
    },
    /**
     * 选择内容可见性选项
     * @param {string} value - 选项值：'公开'/'私密'/'仅互关'
     */
    selectContentVisibility(value) {
      this.tempContentVisibility = value
    },
    /**
     * 保存内容可见性设置
     */
    saveContentVisibility() {
      // 更新设置值
      this.settings.contentVisibility = this.tempContentVisibility
      // 保存到localStorage，刷新后状态不丢失
      localStorage.setItem('contentVisibility', this.tempContentVisibility)
      // 关闭弹窗
      this.closeContentVisibilityModal()
      // 提示设置成功
      this.$message.success('设置成功')
    },
    // 跳转到评论设置
    goToCommentSettings() {
      this.$emit('goToCommentSettings')
    },
    // 跳转到私信设置
    goToMessageSettings() {
      this.$emit('goToMessageSettings')
    },
    // 跳转到隐私协议
    goToPrivacyPolicy() {
      this.$router.push('/yinsi')
    },
    // 充值 - 跳转到个人中心
    recharge() {
      // 检查是否开启未成年人模式
      if (this.$store.state.teenagerMode) {
        this.$message.warning('未成年人模式下禁止充值')
        return
      }
      this.$router.push('/gr')
    },
    // 跳转到兑换礼物
    goToExchangeGifts() {
      this.$emit('goToExchangeGifts')
    },
    // 复制邮箱
    copyEmail(email) {
      // 复制到剪贴板
      if (navigator.clipboard) {
        navigator.clipboard.writeText(email).then(() => {
          this.$message.success('已复制')
        })
      } else {
        // 兼容旧浏览器
        const input = document.createElement('input')
        input.value = email
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$message.success('已复制')
      }
    },
    // 跳转到用户协议
    goToUserAgreement() {
      this.$router.push('/xieyi')
    },
    // 切换评论权限
    toggleCommentPermission() {
      this.settings.commentPermission = this.settings.commentPermission === '所有人' ? '仅互关' : '所有人'
    },
    // 切换私信权限
    toggleMessagePermission() {
      this.settings.messagePermission = this.settings.messagePermission === '所有人' ? '仅互关' : '所有人'
    },
    // ========== 未成年人模式密码管理 ==========
    /**
     * 切换未成年人模式入口
     * 根据当前状态决定是开启（设置密码）还是关闭（验证密码）
     */
    toggleTeenagerMode() {
      // 判断当前是否已开启未成年人模式
      if (this.settings.teenagerMode) {
        // 已开启 → 需要验证密码才能关闭
        this.openTeenagerVerifyModal()
      } else {
        // 未开启 → 开始设置密码流程
        this.openTeenagerSetModal()
      }
    },
    /**
     * 打开设置密码弹窗（开启未成年人模式）
     */
    openTeenagerSetModal() {
      // 重置密码输入和错误信息
      this.teenagerPassword = ''
      this.teenagerPasswordError = ''
      // 设置弹窗模式为设置密码
      this.teenagerModalMode = 'set'
      // 显示弹窗
      this.showTeenagerPasswordModal = true
      // 弹窗显示后自动聚焦到密码输入框
      this.$nextTick(() => {
        const input = this.$refs.teenagerPasswordInput
        if (input) {
          input.focus()
        }
      })
    },
    /**
     * 打开验证密码弹窗（关闭未成年人模式时用）
     */
    openTeenagerVerifyModal() {
      // 清空验证输入和错误信息
      this.teenagerPasswordVerify = ''
      this.teenagerPasswordError = ''
      // 设置弹窗模式为验证密码
      this.teenagerModalMode = 'verify'
      // 显示弹窗
      this.showTeenagerPasswordModal = true
      // 弹窗显示后自动聚焦到密码输入框
      this.$nextTick(() => {
        const input = this.$refs.teenagerPasswordInput
        if (input) {
          input.focus()
        }
      })
    },
    /**
     * 关闭未成年人模式密码弹窗
     */
    closeTeenagerPasswordModal() {
      this.showTeenagerPasswordModal = false
      // 延迟清空数据，避免弹窗关闭动画期间数据清空导致闪烁
      setTimeout(() => {
        this.teenagerPassword = ''
        this.teenagerPasswordVerify = ''
        this.teenagerPasswordError = ''
      }, 300)
    },
    /**
     * 确认开启未成年人模式
     * 只需输入一次4位密码即可开启
     */
    confirmTeenagerPassword() {
      // 验证密码长度
      if (this.teenagerPassword.length !== 4) {
        this.teenagerPasswordError = '请输入4位数字密码'
        return
      }
      // 验证是否为纯数字
      if (!/^\d{4}$/.test(this.teenagerPassword)) {
        this.teenagerPasswordError = '密码必须为4位数字'
        return
      }
      // 密码验证通过，开启未成年人模式
      // 保存密码到localStorage（用于后续关闭验证）
      localStorage.setItem('teenagerPassword', this.teenagerPassword)
      // 保存未成年人模式开启状态
      localStorage.setItem('teenagerMode', 'true')
      // 同步到Vuex
      this.$store.commit('setTeenagerMode', true)
      // 更新页面状态
      this.settings.teenagerMode = true
      // 关闭弹窗
      this.closeTeenagerPasswordModal()
      // 提示开启成功
      this.$message.success('未成年人模式已开启')
    },
    /**
     * 验证密码并关闭未成年人模式
     */
    verifyAndCloseTeenagerMode() {
      // 获取本地存储的正确密码
      const correctPassword = localStorage.getItem('teenagerPassword')
      // 验证输入的密码是否正确
      if (this.teenagerPasswordVerify !== correctPassword) {
        this.teenagerPasswordError = '密码错误，请重新输入'
        // 清空输入框
        this.teenagerPasswordVerify = ''
        return
      }
      // 密码正确，关闭未成年人模式
      // 清除本地存储的密码和状态
      localStorage.removeItem('teenagerPassword')
      localStorage.removeItem('teenagerMode')
      // 同步到Vuex
      this.$store.commit('setTeenagerMode', false)
      // 更新页面状态
      this.settings.teenagerMode = false
      // 关闭弹窗
      this.closeTeenagerPasswordModal()
      // 提示关闭成功
      this.$message.success('未成年人模式已关闭')
    },
    /**
     * 处理密码输入框的键盘输入
     * 使用keyup事件，更可靠地捕获数字输入
     */
    handlePasswordKeyup(field, event) {
      // 获取输入框的值
      const input = event.target
      let value = input.value
      // 过滤非数字字符
      value = value.replace(/\D/g, '')
      // 限制最多4位
      if (value.length > 4) {
        value = value.slice(0, 4)
      }
      // 如果值被修改了，更新输入框显示（防止非数字字符残留）
      if (input.value !== value) {
        input.value = value
      }
      // 更新对应字段
      this[field] = value
      // 清空错误提示（用户开始输入时清除错误）
      if (this.teenagerPasswordError) {
        this.teenagerPasswordError = ''
      }

    },
    /**
     * 点击密码点阵时聚焦到输入框
     */
    focusPasswordInput() {
      const input = this.$refs.teenagerPasswordInput
      if (input) {
        input.focus()
        // 将光标移到末尾
        const len = input.value.length
        input.setSelectionRange(len, len)
      }
    },
    // 跳转到未成年人模式说明
    goToTeenagerDesc() {
      this.$router.push('/teenager-desc')
    }
  }
}
</script>

<style scoped lang="scss">
// 页面主容器
.settings-page {
  display: flex;
  min-height: 100vh;
  background: #f8f9fa;
}

// 左侧侧边栏
.settings-sidebar {
  width: 220px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin: 24px;
  padding: 16px 0;
  flex-shrink: 0;
  height: fit-content;

  // 侧边栏导航
  .sidebar-nav {
    display: flex;
    flex-direction: column;
  }

  // 导航项
  .nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    margin: 0 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #666;
    font-size: 14px;
    border-radius: 8px;

    .nav-icon {
      height: 15px;
    }

    &:hover {
      background: #f9f9f9;
      color: #8B5CF6;
    }

    // 激活状态
    &.active {
      background: #f3f0ff;
      color: #8B5CF6;

      .nav-icon {
        filter: brightness(0) saturate(100%) invert(45%) sepia(100%) saturate(500%) hue-rotate(250deg) brightness(95%) contrast(90%);
      }
    }
  }
}

// 右侧主内容区
.settings-main {
  flex: 1;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin: 24px 24px 24px 0;
  padding: 24px 32px;
  overflow-y: auto;
  height: calc(100vh - 48px);
}

// 设置区块
.settings-section {
  margin-bottom: 32px;

  // 区块标题
  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #333; // 默认黑色
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
    transition: transform 0.3s ease, color 0.3s ease; // 添加过渡效果

    // 选中状态：紫色
    &.active {
      color: #A251F8;
    }

    // 标题放大动画
    &.title-scale-animation {
      animation: titleScale 0.3s ease;
    }
  }

  // 标题放大动画关键帧
  @keyframes titleScale {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
}

// 设置列表
.settings-list {
  display: flex;
  flex-direction: column;
}

// 设置项
.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  cursor: pointer;
  transition: background 0.3s ease;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }

  // 左侧内容区
  .item-left {
    display: flex;
    flex-direction: column;
    gap: 4px;

    // 设置项标题
    .item-title {
      font-size: 14px;
      color: #333;
      font-weight: 500;
    }

    // 设置项描述
    .item-desc {
      font-size: 12px;
      color: #999;
    }

    // 余额显示
    .item-balance {
      font-size: 14px;
      color: #333;
      margin-top: 4px;
    }

    // 联系方式区域
    .item-contact {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 4px;

      // 邮箱链接
      .item-link {
        font-size: 13px;
        color: #8B5CF6;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }

      // 复制按钮
      .copy-btn {
        font-size: 12px;
        color: #8B5CF6;
        cursor: pointer;
        padding: 2px 8px;
        border: 1px solid #8B5CF6;
        border-radius: 4px;

        &:hover {
          background: #f3f0ff;
        }
      }
    }
  }

  // 右侧内容区
  .item-right {
    display: flex;
    align-items: center;
    gap: 12px;

    // 设置项值（如"私密"、"所有人"）
    .item-value {
      font-size: 13px;
      color: #666;

      // 主色调文字（紫色）
      &.text-primary {
        color: #8B5CF6;
      }
    }

    // 箭头图标
    .el-icon-arrow-right {
      font-size: 14px;
      color: #ccc;
    }

    // 自定义开关样式
    .custom-switch {
      cursor: pointer;

      .switch-track {
        width: 44px;
        height: 22px;
        background: #dcdfe6;
        border-radius: 11px;
        position: relative;
        transition: background 0.3s ease;

        .switch-thumb {
          width: 18px;
          height: 18px;
          background: #fff;
          border-radius: 50%;
          position: absolute;
          top: 2px;
          left: 2px;
          transition: transform 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      }

      // 激活状态
      &.active {
        .switch-track {
          background: #8B5CF6;

          .switch-thumb {
            transform: translateX(22px);
          }
        }
      }
    }

    // 充值按钮
    .recharge-btn {
      padding: 6px 20px;
      background: #8B5CF6;
      border: none;
      border-radius: 16px;
      color: #fff;
      font-size: 13px;
      cursor: pointer;
      transition: opacity 0.3s ease;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

// 弹窗遮罩层
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

// 弹窗内容
.modal-content {
  background: #fff;
  border-radius: 16px;
  width: 600px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

// 弹窗头部
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid #f0f0f0;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }

  .el-icon-close {
    font-size: 20px;
    color: #999;
    cursor: pointer;

    &:hover {
      color: #666;
    }
  }
}

// 弹窗主体
.modal-body {
  padding: 24px 32px;
}

// 表单项
.form-item {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 14px;
    color: #333;
    margin-bottom: 6px;
    font-weight: 500;
  }

  input[type="text"],
  input[type="password"],
  input[type="date"],
  textarea {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    color: #333;
    background: #f8f8f8;
    outline: none;
    transition: border-color 0.3s ease;
    box-sizing: border-box;

    &:focus {
      border-color: #8B5CF6;
    }

    &::placeholder {
      color: #999;
    }
  }

  textarea {
    resize: none;
    min-height: 60px;
  }
}

// 账号信息行
.account-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  font-size: 14px;
  color: #666;

  .account-left {
    display: flex;
    gap: 8px;
  }

  .account-text {
    color: #999;
  }

  .account-id {
    color: #666;
  }

  .copy-btn {
    color: #8B5CF6;
    cursor: pointer;
    font-size: 13px;

    &:hover {
      text-decoration: underline;
    }
  }
}

// 性别选项
.gender-options {
  display: flex;
  gap: 32px;

  .radio-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 15px;
    color: #333;

    input[type="radio"] {
      display: none;
    }

    .radio-custom {
      width: 18px;
      height: 18px;
      border: 2px solid #d0d0d0;
      border-radius: 50%;
      position: relative;
      transition: all 0.3s ease;

      &::after {
        content: '';
        width: 10px;
        height: 10px;
        background: #8B5CF6;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0);
        transition: transform 0.3s ease;
      }
    }

    input[type="radio"]:checked + .radio-custom {
      border-color: #8B5CF6;

      &::after {
        transform: translate(-50%, -50%) scale(1);
      }
    }
  }
}

// 日期输入框
.date-input {
  position: relative;

  input[type="date"] {
    width: 100%;
    padding-right: 40px;
    box-sizing: border-box;
  }

  .el-icon-date {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 16px;
    pointer-events: none;
  }
}

// 弹窗底部
.modal-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 16px 32px 24px;

  .btn-primary {
    padding: 10px 32px;
    background: #8B5CF6;
    border: none;
    border-radius: 20px;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.9;
    }
  }

  .btn-default {
    padding: 10px 32px;
    background: #f0f0f0;
    border: none;
    border-radius: 20px;
    color: #666;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #e0e0e0;
    }
  }
}

// ========== 未成年人模式密码弹窗样式 V2 ==========
.teenager-modal-v2 {
  width: 400px;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(180deg, #fff 0%, #faf8ff 100%);

  // 头部样式
  .teenager-modal-header {
    position: relative;
    padding: 32px 24px 16px;
    text-align: center;
    background: linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%);

    .teenager-icon {
      width: 64px;
      height: 64px;
      margin: 0 auto 16px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(10px);
      border: 2px solid rgba(255, 255, 255, 0.3);
      overflow: hidden;

      img {
        width: 32px;
        height: 32px;
        object-fit: contain;
        filter: brightness(0) invert(1);
      }
    }

    h3 {
      font-size: 20px;
      font-weight: 600;
      color: #fff;
      margin: 0;
    }

    .close-btn {
      position: absolute;
      top: 16px;
      right: 16px;
      font-size: 20px;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      transition: color 0.3s ease;

      &:hover {
        color: #fff;
      }
    }
  }

  // 主体样式
  .teenager-modal-body {
    padding: 24px 28px;
    position: relative;

    .teenager-desc {
      font-size: 15px;
      color: #333;
      text-align: center;
      margin-bottom: 8px;
      font-weight: 500;
    }

    .teenager-subdesc {
      font-size: 13px;
      color: #999;
      text-align: center;
      margin-bottom: 20px;
      margin-top: 16px;
    }

    // 功能介绍列表
    .teenager-features {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 8px;

      .feature-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;

        .feature-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;

          svg {
            width: 22px;
            height: 22px;
          }

          &.icon-shield {
            background: linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%);
            color: #fff;
          }

          &.icon-users {
            background: linear-gradient(135deg, #4ECDC4 0%, #6EE7DE 100%);
            color: #fff;
          }

          &.icon-video {
            background: linear-gradient(135deg, #667EEA 0%, #8A9AEF 100%);
            color: #fff;
          }

          &.icon-star {
            background: linear-gradient(135deg, #F7B731 0%, #F9D423 100%);
            color: #fff;
          }
        }

        .feature-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;

          .feature-title {
            font-size: 15px;
            font-weight: 600;
            color: #333;
            line-height: 1.4;
          }

          .feature-desc {
            font-size: 12px;
            color: #999;
            line-height: 1.5;
          }
        }
      }
    }

    // 密码输入容器
    .password-input-container {
      position: relative;
      display: flex;
      justify-content: center;
      cursor: text;
    }

    // 4位密码点阵输入框
    .password-dots {
      display: flex;
      justify-content: center;
      gap: 16px;
      margin-bottom: 8px;
      pointer-events: none;

      .password-dot {
        width: 56px;
        height: 56px;
        border: 2px solid #e0e0e0;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        transition: all 0.3s ease;

        &.filled {
          border-color: #8B5CF6;
          background: #f3f0ff;
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
        }

        &.active {
          border-color: #8B5CF6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
          animation: pulse 1.5s infinite;
        }

        span {
          font-size: 24px;
          color: #8B5CF6;
        }
      }
    }

    // 隐藏的真实输入框
    .hidden-input {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: text;
      font-size: 16px;
      color: transparent;
      background: transparent;
      border: none;
      outline: none;
      caret-color: transparent;
    }

    // 输入框焦点动画
    @keyframes pulse {
      0%, 100% {
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
      }
      50% {
        box-shadow: 0 0 0 6px rgba(139, 92, 246, 0.08);
      }
    }

    // 错误提示
    .teenager-error {
      color: #ff4d4f;
      font-size: 13px;
      text-align: center;
      margin-top: 16px;
      animation: shake 0.5s ease;
    }
  }

  // 底部按钮样式
  .teenager-modal-footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 32px 32px;

    .btn-teenager-primary {
      width: 100%;
      padding: 14px 24px;
      background: linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%);
      border: none;
      border-radius: 12px;
      color: #fff;
      font-size: 15px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
      }

      &:disabled {
        background: #d0d0d0;
        box-shadow: none;
        cursor: not-allowed;
      }
    }

    .btn-teenager-default {
      width: 100%;
      padding: 14px 24px;
      background: transparent;
      border: 1px solid #e0e0e0;
      border-radius: 12px;
      color: #666;
      font-size: 15px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: #f5f5f5;
        border-color: #d0d0d0;
      }
    }
  }
}

// ========== 内容可见性弹窗样式 ==========
.visibility-modal {
  width: 480px;

  .modal-desc {
    font-size: 14px;
    color: #666;
    margin-bottom: 20px;
    line-height: 1.5;
  }

  .visibility-options {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .visibility-option {
      display: flex;
      align-items: center;
      padding: 16px 20px;
      border: 2px solid #e8e8e8;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      background: #fff;

      &:hover {
        border-color: #c8c8c8;
        background: #fafafa;
      }

      &.active {
        border-color: #8B5CF6;
        background: #f3f0ff;

        .option-icon {
          background: #8B5CF6;
          color: #fff;
        }

        .option-title {
          color: #8B5CF6;
        }
      }

      .option-icon {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: #f0f0f0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
        transition: all 0.3s ease;

        i {
          font-size: 20px;
          color: inherit;
        }
      }

      .option-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;

        .option-title {
          font-size: 15px;
          font-weight: 500;
          color: #333;
          transition: color 0.3s ease;
        }

        .option-desc {
          font-size: 13px;
          color: #999;
        }
      }

      .option-check {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 20px;
          color: #8B5CF6;
          font-weight: bold;
        }
      }
    }
  }
}

// 错误提示抖动动画
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}
</style>